import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId, DEFAULT_RATES, inr, RATES_COMING_SOON, RATES_SOON_LABEL } from "@/lib/site";

const LINES = [
  { text: "Crafted for", cls: "text-white" },
  { text: "Royalty.", cls: "gold-text italic" },
  { text: "Consecrated in Gold.", cls: "text-white" },
];

export const Hero = ({ rates }) => {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 900], [0, 140]);
  const yTxt = useTransform(scrollY, [0, 900], [0, -60]);
  const r22 = (rates || DEFAULT_RATES).gold_22k;

  return (
    <header id="hero" data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden pb-24 pt-36">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(circle at 50% 0%, rgba(35,81,157,0.35) 0%, rgba(6,11,24,0) 65%)" }}
      />
      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-16 px-6 lg:grid-cols-12 lg:px-12">
        <motion.div style={{ y: yTxt }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.3, duration: 1 }}
            className="eyebrow mb-8"
          >
            aradhanajewellers.com · Atelier of Fine Gold
          </motion.p>
          <h1 className="font-serif text-4xl leading-[1.02] tracking-tight sm:text-5xl lg:text-7xl">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 2.4 + i * 0.16, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.1, duration: 0.9 }}
            className="mt-8 max-w-xl text-sm font-light leading-relaxed text-[#B5B0A8] sm:text-base"
          >
            Heirloom 22KT bridal sets, IGI-certified solitaires and temple nakshi — hand-finished by
            fourth-generation karigars, hallmarked without compromise.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.3, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button data-testid="hero-explore-button" onClick={() => scrollToId("collections")} className="btn-gold">
              Explore the Collections
            </button>
            <button data-testid="hero-savings-button" onClick={() => scrollToId("scheme")} className="btn-ghost">
              Swarna Nidhi 11+1 Plan
            </button>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yImg }} className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 2.6, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-t-[999px] border border-[#D4AF37]/25 p-3"
          >
            <div className="overflow-hidden rounded-t-[999px]">
              <motion.img
                src="/assets/hero-necklace.png"
                alt="22KT temple bridal choker by Aradhana Jewellers"
                data-testid="hero-spotlight-image"
                className="aspect-[3/4] w-full object-cover"
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 3.5, duration: 0.9 }}
              data-testid="hero-live-rate-badge"
              className="glass-card absolute -left-4 bottom-12 rounded-xl px-5 py-4 sm:-left-10"
            >
              <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-[#99732B]">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#D4AF37]" />
                {RATES_COMING_SOON ? "22K GOLD RATE" : "22K TODAY"}
              </p>
              <p className="mt-1 font-mono text-xl text-[#E6C265]">
                {RATES_COMING_SOON ? <span className="text-base">{RATES_SOON_LABEL}</span> : `${inr(r22)}/g`}
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("collections")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#79756E] transition-colors hover:text-[#E6C265]"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}>
          <ArrowDown size={18} />
        </motion.div>
      </motion.button>
    </header>
  );
};
